import React, { useMemo, useState } from 'react';
import { AlertTriangleIcon, BadgeCheckIcon, CheckIcon, ClipboardListIcon, CloudOffIcon, CopyIcon, LockIcon, PlusIcon, ShieldAlertIcon, TrendingUpIcon, XIcon } from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader';
import { Panel } from '../../components/ui/Panel';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { Meter } from '../../components/ui/Meter';
import { EmptyState } from '../../components/ui/EmptyState';
import { Modal } from '../../components/ui/Modal';
import { Field, Input, Select, Textarea } from '../../components/ui/Field';
import { EvidenceCapture } from '../../components/network/EvidenceCapture';
import { trainingModules, useNetwork } from '../../contexts/NetworkContext';
import { naira } from '../../utils/format';
import { balancesFor, earningTone, MINIMUM_PAYOUT, nextRank, rankAtLeast, scopeFor, scoreFor, taskTone } from '../../utils/network';
import type { CapturedEvidence, FieldTask, IntelCategory } from '../../types/network';
const TABS = ['Overview', 'Tasks', 'Growth', 'Earnings', 'Performance', 'Training', 'Team', 'Safety'] as const;
type Tab = (typeof TABS)[number];
const INTEL_CATEGORIES: IntelCategory[] = ['Accommodation', 'Transport', 'Food', 'Retail', 'Internet', 'Security', 'Utilities', 'Prices', 'Local developments', 'Student needs'];
export function RepDashboard() {
  const network = useNetwork();
  const rep = network.currentRep;
  const [tab, setTab] = useState<Tab>('Overview');
  const [openTaskId, setOpenTaskId] = useState<string | null>(null);
  const [leadOpen, setLeadOpen] = useState(false);
  const [intelOpen, setIntelOpen] = useState(false);
  const [incidentOpen, setIncidentOpen] = useState(false);
  const [quizId, setQuizId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const campus = network.campuses.find(item => item.id === rep.campusId);
  const area = campus?.areas.find(item => item.id === rep.areaId);
  const myTasks = network.tasks.filter(task => task.assignedRepId === rep.id);
  const openTasks = network.tasks.filter(task => task.status === 'Open' && task.campusId === rep.campusId);
  const mySubmissions = network.verifications.filter(item => item.repId === rep.id);
  const myEarnings = network.earnings.filter(entry => entry.repId === rep.id);
  const myReferrals = network.referrals.filter(item => item.repId === rep.id);
  const myLeads = network.leads.filter(item => item.repId === rep.id);
  const myIntel = network.intel.filter(item => item.repId === rep.id);
  const myEvents = network.events.filter(item => item.repId === rep.id);
  const myDisputes = network.disputes.filter(item => item.repId === rep.id);
  const balances = balancesFor(myEarnings);
  const breakdown = useMemo(() => scoreFor(myEvents, mySubmissions, myTasks), [myEvents, mySubmissions, myTasks]);
  const openTask = network.tasks.find(task => task.id === openTaskId) ?? null;
  const team = network.reps.filter(item => item.supervisorRepId === rep.id || rep.rank === 'Campus Lead' && item.campusId === rep.campusId && item.id !== rep.id);
  const onboardingOutstanding = rep.onboarding.filter(step => step.required && !step.done);
  const operational = rep.status === 'Active' || rep.status === 'Probation';
  function claim(task: FieldTask) {
    const rankOk = rankAtLeast(rep.rank, task.minRank);
    const trainingOk = task.requiresTraining.every(moduleId => rep.trainingCompleted.includes(moduleId));
    if (!rankOk || !trainingOk) return;
    network.acceptTask(task.id, rep.id);
    setToast(`${task.id} accepted`);
  }
  function submitEvidence(captured: CapturedEvidence, offline: boolean) {
    if (!openTask) return;
    const result = network.submitVerification(openTask.id, rep.id, captured, offline);
    setOpenTaskId(null);
    setToast(offline ? 'Queued on this device. It will sync when you reconnect.' : result.flags.length > 0 ? `Submitted with ${result.flags.length} review flag(s).` : 'Submitted for review.');
  }
  if (!operational) {
    return <div className="space-y-6" data-id="element-9299" data-mp="3fytxr8v1eew8">
        <PageHeader eyebrow="Campus Representative" title={`Welcome, ${rep.name.split(' ')[0]}`} description="Your account is approved but not yet operational. Complete the required steps to unlock field work." data-id="element-9300" data-mp="3fz29uyjc5g6d" />
        <Panel title="Onboarding" description="Tasks and earnings stay locked until the required steps are done." data-id="element-9301" data-mp="2sa0yp3j4bqxe">
          <ul className="divide-y divide-line" data-id="element-9302" data-mp="2x212j72b73z9">
            {rep.onboarding.map(step => <li key={step.key} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9303" data-mp="20i1xo2r4dxxw">
                <span className="min-w-0 flex-1" data-id="element-9304" data-mp="24jn538kvxk0p">
                  <span className="block text-[13px] font-medium text-ink" data-id="element-9305" data-mp="24ic2hc36eib6">{step.label}</span>
                  <span className="block text-[12px] text-ink-muted" data-id="element-9306" data-mp="1wz3042t37jvm">{step.detail}</span>
                </span>
                {step.done ? <Badge tone="ok" icon={<CheckIcon className="h-3.5 w-3.5" data-id="element-9308" data-mp="1nz675oznzyur" />} data-id="element-9307" data-mp="1nzpqna4yqvaw">
                    Done
                  </Badge> : <Button size="sm" variant={step.required ? 'primary' : 'secondary'} onClick={() => network.completeOnboardingStep(rep.id, step.key)} data-id="element-9309" data-mp="1j3infp3ybxwn">
                    {step.required ? 'Complete' : 'Complete (optional)'}
                  </Button>}
              </li>)}
          </ul>
          <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-9310" data-mp="108ttrylnh9ws">
            {onboardingOutstanding.length === 0 ? 'All required steps are done. Your account activates immediately.' : `${onboardingOutstanding.length} required step${onboardingOutstanding.length === 1 ? '' : 's'} remaining.`}
          </p>
        </Panel>
      </div>;
  }
  return <div className="space-y-6" data-id="element-9311" data-mp="lpsmmsl3xd3j">
      <PageHeader eyebrow={`${campus?.name ?? 'Campus'} · ${rep.repCode}`} title={`${rep.rank}`} description={`${area ? `${area.name} · ` : ''}Reporting to ${network.reps.find(item => item.id === rep.supervisorRepId)?.name ?? 'the campus lead'}. Every submission you make is attributed to you and reviewed before it changes anything on Campuzon.`} actions={<div className="flex flex-wrap gap-2" data-id="element-9313" data-mp="14gjxxzonndkk">
            <Badge tone={rep.status === 'Probation' ? 'warn' : 'ok'} data-id="element-9314" data-mp="porog4bjktqr">{rep.status}</Badge>
            <Badge tone="brand" data-id="element-9315" data-mp="uo4gd3h9sr07">Score {breakdown.score}/100</Badge>
          </div>} data-id="element-9312" data-mp="6qyo99sayxx2" />

      {toast && <div className="rounded-card border border-ok-100 bg-ok-50 px-4 py-3" data-id="element-9316" data-mp="1idp03ol8zsaj">
          <p className="flex items-center gap-2 text-[13px] text-ok-700" data-id="element-9317" data-mp="1n5gte6vsgc4d">
            <CheckIcon className="h-4 w-4 shrink-0" data-id="element-9318" data-mp="37vcfgsji81rh" />
            {toast}
            <button type="button" onClick={() => setToast(null)} className="ml-auto text-[12px] font-medium underline" data-id="element-9319" data-mp="37t9dda52ui6e">
              Dismiss
            </button>
          </p>
        </div>}

      <Tabs tabs={TABS} value={tab} onChange={setTab} label="Representative sections" data-id="element-9320" data-mp="3cndl3vm62u8j" />

      {/* ------------------------------------------------------- overview -- */}
      {tab === 'Overview' && <div className="grid gap-4 lg:grid-cols-3" data-id="element-9321" data-mp="2jyoq8ebpfl58">
          <div className="space-y-4 lg:col-span-2" data-id="element-9322" data-mp="2xxg3jyiqt2sb">
            <ul className="grid gap-3 sm:grid-cols-3" data-id="element-9323" data-mp="2xy7nud6w3e3a">
              {[{
            label: 'Approved earnings',
            value: naira(balances.approved),
            note: 'Awaiting payout'
          }, {
            label: 'Pending review',
            value: naira(balances.pending),
            note: 'Not yet approved'
          }, {
            label: 'Paid to date',
            value: naira(balances.paid),
            note: 'Settled'
          }].map(metric => <li key={metric.label} className="rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-9324" data-mp="22pw1hpep2aq2">
                  <p className="text-[12px] text-ink-muted" data-id="element-9325" data-mp="27np5qcwfsunp">{metric.label}</p>
                  <p className="cz-num mt-1 text-xl font-semibold tracking-tight text-ink" data-id="element-9326" data-mp="2clqnmui4mnxl">
                    {metric.value}
                  </p>
                  <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9327" data-mp="2leu9nr6u893u">{metric.note}</p>
                </li>)}
            </ul>

            <Panel title="What you are authorised to do" description={`${rep.rank} · ${campus?.institution ?? 'campus not assigned'}${area ? ` · ${area.name}` : ''}`} data-id="element-9328" data-mp="2qcvqs6kpdyco">
              <div className="grid gap-4 sm:grid-cols-2" data-id="element-9329" data-mp="30a0eiau761vl">
                <div data-id="element-9330" data-mp="1100m7nygp6o8">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-9331" data-mp="110jxiltlwwta">
                    Authorised
                  </p>
                  <ul className="mt-1.5 space-y-1" data-id="element-9332" data-mp="3j8v8h3gh0ngx">
                    {scopeFor(rep.rank).permitted.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink" data-id="element-9333" data-mp="3egk6lv266rlq">
                        <CheckIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-9334" data-mp="3agt9zapyph9a" /> {item}
                      </li>)}
                  </ul>
                </div>
                <div data-id="element-9335" data-mp="6239bidoi1tg">
                  <p className="text-[12px] font-semibold uppercase tracking-wide text-ink-subtle" data-id="element-9336" data-mp="619xw43ryo4z">
                    Not authorised
                  </p>
                  <ul className="mt-1.5 space-y-1" data-id="element-9337" data-mp="3t69esa8ojvvi">
                    {scopeFor(rep.rank).restricted.map(item => <li key={item} className="flex items-start gap-2 text-[13px] leading-6 text-ink-muted" data-id="element-9338" data-mp="3o6d7a10rxmb7">
                        <XIcon className="mt-1 h-3.5 w-3.5 shrink-0 text-danger-700" data-id="element-9339" data-mp="2hs5kf2y6k5px" /> {item}
                      </li>)}
                  </ul>
                </div>
              </div>
              <p className="mt-3 text-[12px] leading-5 text-ink-muted" data-id="element-9340" data-mp="2vkrd5e56jt5i">
                Authorisation is enforced by the Campuzon services behind this console, not by what this
                screen shows you. It widens as you are promoted.
              </p>
            </Panel>

            <Panel title="What needs you today" data-id="element-9341" data-mp="30it1adhgqinu">
              {myTasks.filter(task => ['Accepted', 'In progress', 'Rework required'].includes(task.status)).length === 0 ? <EmptyState title="Nothing outstanding" description="Open tasks for your campus are in the Tasks section." action={<Button variant="primary" size="sm" onClick={() => setTab('Tasks')} data-id="element-9343" data-mp="3jalsc2cz1tsx">
                      See available tasks
                    </Button>} data-id="element-9342" data-mp="39al4c345qpie" /> : <ul className="divide-y divide-line" data-id="element-9344" data-mp="1owuj33yk1by0">
                  {myTasks.filter(task => ['Accepted', 'In progress', 'Rework required'].includes(task.status)).map(task => <li key={task.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9345" data-mp="9eoc4p6xpugq">
                        <span className="min-w-0 flex-1" data-id="element-9346" data-mp="3qr8tdtqrj8yp">
                          <span className="block text-[13px] font-medium text-ink" data-id="element-9347" data-mp="3vqv6m36lcp7i">{task.title}</span>
                          <span className="block text-[12px] text-ink-muted" data-id="element-9348" data-mp="3hrltxdhlgd8a">
                            {task.type} · due {task.dueOn}
                            {task.reviewNotes ? ` · reviewer: ${task.reviewNotes}` : ''}
                          </span>
                        </span>
                        <Badge tone={taskTone(task.status)} data-id="element-9349" data-mp="3cud6oyhl9j06">{task.status}</Badge>
                        <Button size="sm" variant="primary" onClick={() => setOpenTaskId(task.id)} data-id="element-9350" data-mp="162ccptayd4cj">
                          {task.status === 'Rework required' ? 'Recapture' : 'Capture evidence'}
                        </Button>
                      </li>)}
                </ul>}
            </Panel>

            <Panel title="Your recent submissions" data-id="element-9351" data-mp="14k405njde74z">
              {mySubmissions.length === 0 ? <EmptyState title="No submissions yet" description="Your first verification will appear here." data-id="element-9352" data-mp="zn44m6k5cms4" /> : <ul className="divide-y divide-line" data-id="element-9353" data-mp="upvm24v36gqc">
                  {mySubmissions.slice(0, 5).map(submission => <li key={submission.id} className="py-3 first:pt-0" data-id="element-9354" data-mp="1nbn2ds5klh5y">
                      <p className="flex flex-wrap items-center gap-2" data-id="element-9355" data-mp="1nej1vkx2gdwi">
                        <span className="text-[13px] font-medium text-ink" data-id="element-9356" data-mp="1ijn7wf89rggr">{submission.propertyRef}</span>
                        <Badge tone={submission.status === 'Approved' ? 'ok' : submission.status === 'Rejected' ? 'danger' : 'warn'} data-id="element-9357" data-mp="1dm3l2qxi9vd7">
                          {submission.status}
                        </Badge>
                        {submission.syncState !== 'Synced' && <Badge tone="warn" icon={<CloudOffIcon className="h-3.5 w-3.5" data-id="element-9359" data-mp="2sj6gi858yhpt" />} data-id="element-9358" data-mp="2nmfxbby5b606">
                            {submission.syncState}
                          </Badge>}
                        <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-9360" data-mp="2xgq3pxjxa4tw">
                          {submission.submittedOn}
                        </span>
                      </p>
                      {submission.flags.length > 0 && <p className="mt-1 text-[12px] leading-5 text-warn-700" data-id="element-9361" data-mp="3b8w9qqtd2vfm">
                          {submission.flags.length} review flag(s): {submission.flags[0]}
                        </p>}
                      {submission.reviewNotes && <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-9362" data-mp="ltkyaqg0vdo2">
                          Reviewer: {submission.reviewNotes}
                        </p>}
                      {submission.syncState === 'Queued offline' && <Button size="sm" variant="secondary" className="mt-2" onClick={() => network.syncSubmission(submission.id)} data-id="element-9363" data-mp="vm0jrxqj2kdp">
                          Sync now
                        </Button>}
                    </li>)}
                </ul>}
            </Panel>
          </div>

          <div className="space-y-4" data-id="element-9364" data-mp="7tk05ir56kca">
            <Panel title="Your referral identity" data-id="element-9365" data-mp="7wpmwrcata4u">
              <p className="cz-num text-lg font-semibold text-ink" data-id="element-9366" data-mp="1n9shx71hyaw2">{rep.referralCode}</p>
              <p className="mt-1 break-all text-[12px] text-ink-muted" data-id="element-9367" data-mp="1j8gosrjez7j4">
                campuzon.app/join?r={rep.referralCode}
              </p>
              <Button size="sm" variant="secondary" className="mt-2.5" icon={<CopyIcon className="h-3.5 w-3.5" data-id="element-9369" data-mp="1gv3dpx3iah6o" />} onClick={() => {
            navigator.clipboard?.writeText(`https://campuzon.app/join?r=${rep.referralCode}`);
            setToast('Referral link copied');
          }} data-id="element-9368" data-mp="1qv42vzo23ris">
                Copy link
              </Button>
              <ul className="mt-3 space-y-1 border-t border-line pt-3" data-id="element-9370" data-mp="2eivf739lk4k2">
                {(['Click', 'Registered', 'Verified account', 'First action', 'Transacted'] as const).map(stage => <li key={stage} className="flex items-baseline justify-between gap-3" data-id="element-9371" data-mp="24rhjz7oohqwq">
                      <span className="text-[12px] text-ink-muted" data-id="element-9372" data-mp="2shbv9ktcn7ev">{stage}</span>
                      <span className="cz-num text-[12px] font-semibold text-ink" data-id="element-9373" data-mp="2x9mwcqztsz90">
                        {myReferrals.filter(item => item.stage === stage).length}
                      </span>
                    </li>)}
              </ul>
              <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9374" data-mp="2y4dq8rzkrynz">
                A referral earns only once the account reaches its first meaningful action. Referrals
                from your own devices are flagged and not attributed.
              </p>
            </Panel>

            <Panel title="Progression" data-id="element-9375" data-mp="2o8aqttw3ye4q">
              {nextRank(rep.rank) ? <>
                  <p className="text-[13px] text-ink" data-id="element-9376" data-mp="2jdenvypc26cu">
                    Next: <span className="font-semibold" data-id="element-9377" data-mp="2eh7pcqz9nx4x">{nextRank(rep.rank)}</span>
                  </p>
                  <ul className="mt-2.5 space-y-2" data-id="element-9378" data-mp="2ag48q29e9x2n">
                    {[{
                label: 'Score of 85 or above',
                met: breakdown.score >= 85,
                detail: `Currently ${breakdown.score}`
              }, {
                label: '25 approved verifications',
                met: mySubmissions.filter(item => item.status === 'Approved').length >= 25,
                detail: `${mySubmissions.filter(item => item.status === 'Approved').length} approved this account`
              }, {
                label: 'Leadership training complete',
                met: rep.trainingCompleted.includes('trn-leadership'),
                detail: rep.trainingCompleted.includes('trn-leadership') ? 'Done' : 'Not started'
              }, {
                label: 'No open policy warnings',
                met: rep.warnings === 0,
                detail: rep.warnings === 0 ? 'Clean record' : `${rep.warnings} warning(s)`
              }].map(criterion => <li key={criterion.label} className="flex items-start gap-2" data-id="element-9379" data-mp="3emi49svh8ze8">
                        {criterion.met ? <CheckIcon className="mt-0.5 h-4 w-4 shrink-0 text-ok-700" data-id="element-9380" data-mp="232fmxa2qzf5r" /> : <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-subtle" data-id="element-9381" data-mp="2hzq5t4hpznbi" />}
                        <span className="min-w-0" data-id="element-9382" data-mp="2czkcljvsxsrb">
                          <span className="block text-[13px] text-ink" data-id="element-9383" data-mp="2r04tgai5pei6">{criterion.label}</span>
                          <span className="block text-[12px] text-ink-subtle" data-id="element-9384" data-mp="2m11p6sa03cb9">{criterion.detail}</span>
                        </span>
                      </li>)}
                  </ul>
                  <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9385" data-mp="2vqux9jaxp9ar">
                    Promotion is decided by your lead against these criteria. It is never automatic, and
                    any override is recorded with a reason.
                  </p>
                </> : <p className="text-[13px] text-ink-muted" data-id="element-9386" data-mp="3odkwuydmmpvk">You are at the top of the network hierarchy.</p>}
            </Panel>
          </div>
        </div>}

      {/* ---------------------------------------------------------- tasks -- */}
      {tab === 'Tasks' && <>
          {openTask ? <EvidenceCapture task={openTask} onSubmit={submitEvidence} onCancel={() => setOpenTaskId(null)} data-id="element-9387" data-mp="3kayahczq5x5r" /> : <div className="space-y-4" data-id="element-9388" data-mp="3ae3fv56kogwr">
              <Panel title="Your tasks" data-id="element-9389" data-mp="3af4she38lbgl">
                {myTasks.length === 0 ? <EmptyState title="No tasks assigned yet" description="Claim one from the list below." data-id="element-9390" data-mp="2wn6sptvf0rx3" /> : <ul className="divide-y divide-line" data-id="element-9391" data-mp="30o0gmn8uy6pz">
                    {myTasks.map(task => <li key={task.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9392" data-mp="2mq2b87cilkmm">
                        <span className="min-w-0 flex-1" data-id="element-9393" data-mp="2rp59n8xzoslm">
                          <span className="block text-[13px] font-medium text-ink" data-id="element-9394" data-mp="2qqlz0br3vpbx">{task.title}</span>
                          <span className="cz-num block text-[12px] text-ink-muted" data-id="element-9395" data-mp="2uu1qyus8d1ia">
                            {task.id} · {task.type} · due {task.dueOn}
                          </span>
                          {task.reviewNotes && <span className="mt-0.5 block text-[12px] leading-5 text-warn-700" data-id="element-9396" data-mp="2luezb0paza2f">
                              Reviewer: {task.reviewNotes}
                            </span>}
                        </span>
                        <Badge tone={taskTone(task.status)} data-id="element-9397" data-mp="2zrbokxprwjh7">{task.status}</Badge>
                        {['Accepted', 'In progress', 'Rework required'].includes(task.status) && <Button size="sm" variant="primary" onClick={() => setOpenTaskId(task.id)} data-id="element-9398" data-mp="3sgijzntxksy8">
                            Capture
                          </Button>}
                      </li>)}
                  </ul>}
              </Panel>

              <Panel title="Available at your campus" description="Restricted task types stay locked until your rank and training allow them." data-id="element-9399" data-mp="1gugrzouywt99">
                {openTasks.length === 0 ? <EmptyState title="Nothing open right now" description="New tasks appear as operations creates them." data-id="element-9400" data-mp="1qka20lfgp0be" /> : <ul className="space-y-2.5" data-id="element-9401" data-mp="xv2tctmamucf">
                    {openTasks.map(task => {
              const rankOk = rankAtLeast(rep.rank, task.minRank);
              const missingTraining = task.requiresTraining.filter(moduleId => !rep.trainingCompleted.includes(moduleId));
              const locked = !rankOk || missingTraining.length > 0;
              return <li key={task.id} className="rounded-card border border-line-strong bg-surface px-4 py-3.5" data-id="element-9402" data-mp="2iael8grhjhb1">
                          <div className="flex flex-wrap items-center gap-2" data-id="element-9403" data-mp="1kui68ns3q3ra">
                            <p className="text-[13px] font-semibold text-ink" data-id="element-9404" data-mp="1oz82weul3md9">{task.title}</p>
                            <Badge tone={task.priority === 'Urgent' ? 'danger' : task.priority === 'Priority' ? 'warn' : 'neutral'} data-id="element-9405" data-mp="1tvyhexqqq9wk">
                              {task.priority}
                            </Badge>
                            <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-9406" data-mp="djb1vs9h1bzo">{task.id}</span>
                          </div>
                          <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-9407" data-mp="ig9si0txf0t4">{task.detail}</p>
                          <p className="mt-1 text-[12px] text-ink-subtle" data-id="element-9408" data-mp="axtjmqtscvdr">
                            {task.type} · due {task.dueOn} · reward{' '}
                            {naira(network.rules.find(rule => rule.id === task.rewardRuleId)?.amount ?? 0)} on
                            approval
                          </p>
                          {locked ? <p className="mt-2 flex items-start gap-2 text-[12px] leading-5 text-warn-700" data-id="element-9409" data-mp="3o54zxqeem1e7">
                              <LockIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-9410" data-mp="3jd3rygo9qwtz" />
                              {!rankOk ? `Restricted to ${task.minRank} and above.` : `Complete ${missingTraining.map(moduleId => trainingModules.find(module => module.id === moduleId)?.title ?? moduleId).join(' and ')} to unlock this.`}
                            </p> : <Button size="sm" variant="primary" className="mt-2" onClick={() => claim(task)} data-id="element-9411" data-mp="umut6pelfq50">
                              Accept task
                            </Button>}
                        </li>;
            })}
                  </ul>}
              </Panel>
            </div>}
        </>}

      {/* --------------------------------------------------------- growth -- */}
      {tab === 'Growth' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-9412" data-mp="gls56741g5rg">
          <Panel title="Leads" description="Landlords, hostels and businesses you have brought into the pipeline." data-id="element-9413" data-mp="goo520zg54ij">
            <Button size="sm" variant="primary" icon={<PlusIcon className="h-3.5 w-3.5" data-id="element-9415" data-mp="2l4022l26f90q" />} onClick={() => setLeadOpen(true)} data-id="element-9414" data-mp="2azjltqksbpwe">
              Add a lead
            </Button>
            {myLeads.length === 0 ? <div className="mt-3" data-id="element-9416" data-mp="2txd3kbeb3iyf">
                <EmptyState title="No leads yet" description="Leads you add enter the acquisition pipeline." data-id="element-9417" data-mp="2tyee8ereu3fm" />
              </div> : <ul className="mt-3 divide-y divide-line" data-id="element-9418" data-mp="33ur574vkm36o">
                {myLeads.map(lead => <li key={lead.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9419" data-mp="2yw6fhzdy31wh">
                    <span className="min-w-0 flex-1" data-id="element-9420" data-mp="3cvfjm0ovm5k3">
                      <span className="block text-[13px] font-medium text-ink" data-id="element-9421" data-mp="3cuwejkkbrbnh">{lead.name}</span>
                      <span className="block text-[12px] text-ink-muted" data-id="element-9422" data-mp="38um0dkmdpexj">
                        {lead.kind} · {lead.contactMasked} · follow up {lead.followUpOn}
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-5 text-ink-subtle" data-id="element-9423" data-mp="1ehno4vh4c87k">{lead.notes}</span>
                    </span>
                    <Badge tone={lead.stage === 'Verified' || lead.stage === 'Active' ? 'ok' : 'brand'} data-id="element-9424" data-mp="3dn770acz58qe">
                      {lead.stage}
                    </Badge>
                    {!['Verified', 'Active', 'Lost'].includes(lead.stage) && <Button size="sm" variant="secondary" onClick={() => network.advanceLead(lead.id)} data-id="element-9425" data-mp="3sj54px2jql4q">
                        Advance
                      </Button>}
                  </li>)}
              </ul>}
            <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9426" data-mp="a4dzofashg4o">
              Contact numbers are masked in this view. Only the acquisition team sees them in full.
            </p>
          </Panel>

          <Panel title="Campus intelligence" description="Structured observations. Nothing you submit becomes public until it is reviewed." data-id="element-9427" data-mp="k2jxolth6249">
            <Button size="sm" variant="primary" icon={<PlusIcon className="h-3.5 w-3.5" data-id="element-9429" data-mp="28jtklvoytivt" />} onClick={() => setIntelOpen(true)} data-id="element-9428" data-mp="o1izvyntnks0">
              Submit an observation
            </Button>
            {myIntel.length === 0 ? <div className="mt-3" data-id="element-9430" data-mp="1zn2stvhg4q71">
                <EmptyState title="Nothing submitted yet" description="Prices, transport and supply changes are the most useful." data-id="element-9431" data-mp="1umy9zyt2fjdn" />
              </div> : <ul className="mt-3 divide-y divide-line" data-id="element-9432" data-mp="2nau3yvr92x6q">
                {myIntel.map(report => <li key={report.id} className="py-3 first:pt-0" data-id="element-9433" data-mp="2ik3xtgb02qaz">
                    <p className="flex flex-wrap items-center gap-2" data-id="element-9434" data-mp="2ii9e25bgt48h">
                      <Badge tone="neutral" data-id="element-9435" data-mp="2djzivupqq3mv">{report.category}</Badge>
                      <Badge tone={report.status === 'Published' || report.status === 'Verified' ? 'ok' : report.status === 'Rejected' ? 'danger' : 'warn'} data-id="element-9436" data-mp="3b04yhvi0bvcf">
                        {report.status}
                      </Badge>
                      <span className="ml-auto text-[12px] text-ink-subtle" data-id="element-9437" data-mp="25mqey6mr6odp">
                        {report.confidence} confidence
                      </span>
                    </p>
                    <p className="mt-1 text-[13px] leading-6 text-ink" data-id="element-9438" data-mp="2xdo2kwa9jisb">{report.observation}</p>
                    <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9439" data-mp="32bxw72gcabbt">
                      Observed {report.observedOn}
                      {report.expiresOn ? ` · expires ${report.expiresOn}` : ' · no expiry set'}
                    </p>
                  </li>)}
              </ul>}
          </Panel>
        </div>}

      {/* ------------------------------------------------------- earnings -- */}
      {tab === 'Earnings' && <div className="space-y-4" data-id="element-9440" data-mp="19etzw5sc2hjq">
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-9441" data-mp="19dsqe5qyu344">
            {[{
          label: 'Pending',
          value: balances.pending,
          note: 'Awaiting approval'
        }, {
          label: 'Approved',
          value: balances.approved,
          note: 'Not yet paid'
        }, {
          label: 'Paid',
          value: balances.paid,
          note: 'Settled'
        }, {
          label: 'Lifetime',
          value: balances.lifetime,
          note: 'Approved and paid'
        }].map(metric => <li key={metric.label} className="rounded-card border border-line bg-surface px-4 py-3.5" data-id="element-9442" data-mp="qlzzcgvgirz1">
                <p className="text-[12px] text-ink-muted" data-id="element-9443" data-mp="10e5n1eeoen8f">{metric.label}</p>
                <p className="cz-num mt-1 text-xl font-semibold tracking-tight text-ink" data-id="element-9444" data-mp="10g8l8lpzfmo8">
                  {naira(metric.value)}
                </p>
                <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9445" data-mp="7x2z7wddp58w">{metric.note}</p>
              </li>)}
          </ul>

          <Panel title="Earnings ledger" description="Append-only. A correction creates a new entry rather than editing history." data-id="element-9446" data-mp="2y8j526spush">
            {myEarnings.length === 0 ? <EmptyState title="No earnings yet" description="An approved submission is what creates an entry here." data-id="element-9447" data-mp="2scdnt4qngk4d" /> : <ul className="divide-y divide-line" data-id="element-9448" data-mp="2nc94z829rdaz">
                {myEarnings.map(entry => {
            const rule = network.rules.find(item => item.id === entry.ruleId);
            return <li key={entry.id} className="py-3 first:pt-0" data-id="element-9449" data-mp="3g0n3dox9sbn2">
                      <p className="flex flex-wrap items-baseline gap-2" data-id="element-9450" data-mp="3b4fv3pheio1y">
                        <span className="min-w-0 flex-1 text-[13px] font-medium text-ink" data-id="element-9451" data-mp="363s7ldsdspch">
                          {entry.activity}
                        </span>
                        <Badge tone={earningTone(entry.status)} data-id="element-9452" data-mp="1upoba0ybmp49">{entry.status}</Badge>
                        <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-9453" data-mp="1unbonahmbqkv">
                          {naira(entry.grossAmount + entry.adjustment)}
                        </span>
                      </p>
                      <p className="cz-num mt-0.5 text-[12px] text-ink-subtle" data-id="element-9454" data-mp="a7lzxlu3de6w">
                        {entry.id} · {entry.sourceRef} · rule: {rule?.label ?? entry.ruleId} · created{' '}
                        {entry.createdOn}
                        {entry.approvedBy ? ` · approved by ${entry.approvedBy}` : ''}
                        {entry.paidOn ? ` · paid ${entry.paidOn}` : ''}
                      </p>
                      {entry.reversalOf && <p className="mt-0.5 text-[12px] text-ink-muted" data-id="element-9455" data-mp="tts1b9mnrn0m">
                          Adjustment against {entry.reversalOf}. {entry.reason}
                        </p>}
                      {entry.status === 'Reversed' && entry.reason && <p className="mt-0.5 text-[12px] text-danger-700" data-id="element-9456" data-mp="2zpuzrgvtaxef">{entry.reason}</p>}
                    </li>;
          })}
              </ul>}
          </Panel>

          <Panel title="Payout" data-id="element-9457" data-mp="btftv10qslu9">
            <p className="text-[13px] text-ink" data-id="element-9458" data-mp="6ucmvb17c5jn">
              Withdrawable now: <span className="cz-num font-semibold" data-id="element-9459" data-mp="6w5wmz7435vv">{naira(balances.approved)}</span>
            </p>
            <p className="mt-1 text-[12px] leading-5 text-ink-muted" data-id="element-9460" data-mp="krrg6quksxgw">
              Minimum payout {naira(MINIMUM_PAYOUT)}. Payouts are released by finance in settlement
              windows and move through a licensed payment provider — Campuzon does not hold your funds.
              Your bank details live with that provider, not in this console.
            </p>
            <Button variant="primary" size="sm" className="mt-2.5" disabled={!network.flags.payouts || balances.approved < MINIMUM_PAYOUT} data-id="element-9461" data-mp="13j26b5sh967q">
              {!network.flags.payouts ? 'Payouts not yet enabled on this network' : balances.approved < MINIMUM_PAYOUT ? `Below the ${naira(MINIMUM_PAYOUT)} minimum` : 'Request payout'}
            </Button>
          </Panel>

          <Panel title="Disputes" description="Dispute a rejection, a reward or a score. A human decides." data-id="element-9462" data-mp="1x30cd8klstqf">
            {myDisputes.length === 0 ? <EmptyState title="No disputes open" description="You can raise one from any rejected submission." data-id="element-9463" data-mp="1n65g6wbsz5fb" /> : <ul className="divide-y divide-line" data-id="element-9464" data-mp="19dph26m0vrjo">
                {myDisputes.map(dispute => <li key={dispute.id} className="py-3 first:pt-0" data-id="element-9465" data-mp="1e6ikmr5r8tu6">
                    <p className="flex flex-wrap items-center gap-2" data-id="element-9466" data-mp="zgt7ryoav1e2">
                      <span className="text-[13px] font-medium text-ink" data-id="element-9467" data-mp="zeghmy8e1kpx">{dispute.about}</span>
                      <Badge tone={dispute.state === 'Upheld' ? 'ok' : dispute.state === 'Declined' ? 'danger' : 'warn'} data-id="element-9468" data-mp="14djlwggjnmwu">
                        {dispute.state}
                      </Badge>
                      <span className="cz-num ml-auto text-[12px] text-ink-subtle" data-id="element-9469" data-mp="ugoltt4mhegg">{dispute.ref}</span>
                    </p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-9470" data-mp="if6v4y2tgvrz">{dispute.argument}</p>
                    {dispute.decision && <p className="mt-1 text-[12px] leading-5 text-ink" data-id="element-9471" data-mp="dnf5qqvecw3f">Decision: {dispute.decision}</p>}
                  </li>)}
              </ul>}
            {mySubmissions.some(item => ['Rejected', 'Rework required'].includes(item.status)) && <Button size="sm" variant="secondary" className="mt-3" onClick={() => {
          const target = mySubmissions.find(item => ['Rejected', 'Rework required'].includes(item.status))!;
          network.raiseDispute({
            repId: rep.id,
            about: 'Rejected verification',
            ref: target.id,
            argument: 'I disagree with this decision and would like it looked at again.'
          });
          setToast('Dispute raised');
        }} data-id="element-9472" data-mp="3vta8dryitfwr">
                Dispute a decision
              </Button>}
          </Panel>
        </div>}

      {/* ---------------------------------------------------- performance -- */}
      {tab === 'Performance' && <div className="grid gap-4 lg:grid-cols-3" data-id="element-9473" data-mp="19nqhvynlj3bz">
          <Panel className="lg:col-span-2" title="How your score is calculated" data-id="element-9474" data-mp="1doahd8vn6j1a">
            <p className="cz-num text-3xl font-semibold tracking-tight text-ink" data-id="element-9475" data-mp="1dqd6zrsvua5h">
              {breakdown.score}
              <span className="text-base font-normal text-ink-subtle" data-id="element-9476" data-mp="1nmggxjtz7d2z">/100</span>
            </p>
            <ul className="mt-4 space-y-3" data-id="element-9477" data-mp="1nk3tir5g8aij">
              {breakdown.parts.map(part => <li key={part.label} data-id="element-9478" data-mp="2u4wje3ylzvzd">
                  <p className="flex flex-wrap items-baseline justify-between gap-2" data-id="element-9479" data-mp="2z0u04bdsb4co">
                    <span className="text-[13px] font-medium text-ink" data-id="element-9480" data-mp="29cycx845y77r">{part.label}</span>
                    <span className="cz-num text-[12px] text-ink-subtle" data-id="element-9481" data-mp="24en2ow9yhup0">
                      weight {part.weight}% ·{' '}
                      {part.achieved < 0 ? 'not yet measurable' : `${part.achieved}%`}
                    </span>
                  </p>
                  {part.achieved >= 0 && <div className="mt-1" data-id="element-9482" data-mp="1qmqcixruluv9">
                      <Meter value={part.achieved} data-id="element-9483" data-mp="1qnhxzfrqec7t" />
                    </div>}
                  <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9484" data-mp="1lpgggz9yekyg">{part.note}</p>
                </li>)}
            </ul>
            <p className="mt-3.5 text-[12px] leading-5 text-ink-muted" data-id="element-9485" data-mp="20emb09xujn00">
              Components with no data yet are excluded rather than counted as zero. You cannot edit this
              score, and an administrator adjustment requires a recorded reason.
            </p>
          </Panel>
          <div className="space-y-4" data-id="element-9486" data-mp="12vl06sl49kpp">
            <Panel title="Strengths" data-id="element-9487" data-mp="3ekheypqkfhb9">
              {breakdown.strengths.length === 0 ? <p className="text-[13px] text-ink-muted" data-id="element-9488" data-mp="3jbzhvjc5fryv">Not enough reviewed work to say yet.</p> : <ul className="space-y-1" data-id="element-9489" data-mp="34nmisdhrlt05">
                  {breakdown.strengths.map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-ink" data-id="element-9490" data-mp="39mpm9ti3jr60">
                      <TrendingUpIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ok-700" data-id="element-9491" data-mp="15l0faeo8zvq" /> {item}
                    </li>)}
                </ul>}
            </Panel>
            <Panel title="Worth improving" data-id="element-9492" data-mp="3obc0tszwgew6">
              {breakdown.improve.length === 0 ? <p className="text-[13px] text-ink-muted" data-id="element-9493" data-mp="3sbe4g7p73ie3">Nothing below the threshold.</p> : <ul className="space-y-1" data-id="element-9494" data-mp="2cuc8wyhgs22o">
                  {breakdown.improve.map(item => <li key={item} className="flex items-start gap-2 text-[13px] text-ink" data-id="element-9495" data-mp="2gy1v5hao27kj">
                      <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-9496" data-mp="38lmtqyxjdeet" /> {item}
                    </li>)}
                </ul>}
            </Panel>
          </div>
        </div>}

      {/* ------------------------------------------------------- training -- */}
      {tab === 'Training' && <div className="space-y-3" data-id="element-9497" data-mp="2un5bvhubaw4t">
          {trainingModules.map(module => {
        const done = rep.trainingCompleted.includes(module.id);
        return <div key={module.id} className="rounded-card border border-line bg-surface px-4 py-4" data-id="element-9498" data-mp="3n9zppvwmix9b">
                <div className="flex flex-wrap items-center gap-2" data-id="element-9499" data-mp="3igwwxpzo7ru6">
                  <p className="text-[14px] font-semibold text-ink" data-id="element-9500" data-mp="3diln3f9dlhby">{module.title}</p>
                  {module.required && <Badge tone="neutral" data-id="element-9501" data-mp="eupr4xry0a6a">Required</Badge>}
                  {done ? <Badge tone="ok" icon={<BadgeCheckIcon className="h-3.5 w-3.5" data-id="element-9503" data-mp="a1d6szuaqpjc" />} data-id="element-9502" data-mp="a2o7gqsg3h68">
                      Certificate issued
                    </Badge> : <Badge tone="warn" data-id="element-9504" data-mp="mcxjm5wr4s5p">Not complete</Badge>}
                  <span className="ml-auto text-[12px] text-ink-subtle" data-id="element-9505" data-mp="mf0d4zx453f6">{module.minutes} min</span>
                </div>
                <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-9506" data-mp="vdls6l3dm0b7">{module.detail}</p>
                {module.unlocks.length > 0 && <p className="mt-1 text-[12px] text-ink-subtle" data-id="element-9507" data-mp="10cf96nhvg9g4">
                    Unlocks: {module.unlocks.join(', ')}
                  </p>}
                <Button size="sm" variant={done ? 'secondary' : 'primary'} className="mt-2.5" onClick={() => setQuizId(module.id)} data-id="element-9508" data-mp="1a28b0ws7pa76">
                  {done ? 'Retake assessment' : 'Take assessment'}
                </Button>
              </div>;
      })}
        </div>}

      {/* ----------------------------------------------------------- team -- */}
      {tab === 'Team' && <Panel title="Your team" description="You see only representatives you supervise, and only their operational performance." data-id="element-9509" data-mp="2y20usth84s3h">
          {team.length === 0 ? <EmptyState title="You do not supervise anyone yet" description="Team visibility opens at Area Lead." data-id="element-9510" data-mp="37ru57r5mr165" /> : <ul className="divide-y divide-line" data-id="element-9511" data-mp="2a6g0qzds502y">
              {team.map(member => {
          const memberScore = scoreFor(network.events.filter(item => item.repId === member.id), network.verifications.filter(item => item.repId === member.id), network.tasks.filter(item => item.assignedRepId === member.id));
          return <li key={member.id} className="flex flex-wrap items-center gap-2 py-3 first:pt-0" data-id="element-9512" data-mp="4b4wpzmhlhdu">
                    <span className="min-w-0 flex-1" data-id="element-9513" data-mp="1ot5y2rx3c6kx">
                      <span className="block text-[13px] font-medium text-ink" data-id="element-9514" data-mp="1osmez7vpfc5b">{member.name}</span>
                      <span className="cz-num block text-[12px] text-ink-muted" data-id="element-9515" data-mp="1jv4f06zo079g">
                        {member.repCode} · {member.rank}
                      </span>
                    </span>
                    <Badge tone={member.status === 'Active' ? 'ok' : 'warn'} data-id="element-9516" data-mp="1a3zzn662uke3">{member.status}</Badge>
                    <span className="cz-num text-[13px] font-semibold text-ink" data-id="element-9517" data-mp="156zs0qtkx9kv">
                      {memberScore.score}/100
                    </span>
                  </li>;
        })}
            </ul>}
          <p className="mt-2.5 flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-9518" data-mp="r5ngcrlgbu4s">
            <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-9519" data-mp="r802zi25mso6" />
            Personal contact details, payout information and wellbeing data are never visible to a lead.
          </p>
        </Panel>}

      {/* --------------------------------------------------------- safety -- */}
      {tab === 'Safety' && <div className="grid gap-4 lg:grid-cols-2" data-id="element-9520" data-mp="olhf4ro82hrx">
          <Panel title="Report an incident" description="Confidential where you ask for it." data-id="element-9521" data-mp="on27qbqz7qn5">
            <Button variant="primary" size="sm" onClick={() => setIncidentOpen(true)} data-id="element-9522" data-mp="9u8i3xhzpu4r">
              Report an incident
            </Button>
            <ul className="mt-3 divide-y divide-line" data-id="element-9523" data-mp="12kqrfg9b4rqm">
              {network.incidents.filter(incident => incident.repId === rep.id).map(incident => <li key={incident.id} className="py-3 first:pt-0" data-id="element-9524" data-mp="snccjl318b0f">
                    <p className="flex flex-wrap items-center gap-2" data-id="element-9525" data-mp="sp70z9diil99">
                      <span className="text-[13px] font-medium text-ink" data-id="element-9526" data-mp="xk2qa1rd6uio">{incident.kind}</span>
                      <Badge tone={incident.status === 'Resolved' ? 'ok' : 'warn'} data-id="element-9527" data-mp="3dbiac5a99hqa">{incident.status}</Badge>
                      {incident.confidential && <Badge tone="neutral" data-id="element-9528" data-mp="20ejckyeztwrg">Confidential</Badge>}
                    </p>
                    <p className="mt-1 text-[13px] leading-6 text-ink-muted" data-id="element-9529" data-mp="20gclkkd2wt2m">{incident.detail}</p>
                    <p className="mt-0.5 text-[12px] text-ink-subtle" data-id="element-9530" data-mp="1vhja0xhcrc53">
                      Reported {incident.reportedOn}
                      {incident.handledBy ? ` · with ${incident.handledBy}` : ' · not yet picked up'}
                    </p>
                  </li>)}
            </ul>
          </Panel>

          <Panel title="Field safety rules" data-id="element-9531" data-mp="1cp75q57520sb">
            <ul className="space-y-2" data-id="element-9532" data-mp="1cq8l2lv6t9fu">
              {['Never enter an occupied room alone, and never after dark.', 'Tell your area lead where you are going before an inspection.', 'Never pay an “inspection fee”. Campuzon does not reimburse them and no legitimate agent charges one.', 'Leave immediately if you are pressured, followed or threatened, then report it.', 'Never present yourself as an officer or agent of the university.'].map(rule => <li key={rule} className="flex items-start gap-2 text-[12px] leading-5 text-ink-muted" data-id="element-9533" data-mp="1bmi58gaptl1w">
                  <ShieldAlertIcon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warn-700" data-id="element-9534" data-mp="1gm4g4j32mp7j" />
                  {rule}
                </li>)}
            </ul>
            <p className="mt-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9535" data-mp="1lenwtox220gk">
              Some task categories are restricted for safety and cannot be self-claimed, regardless of
              your score.
            </p>
          </Panel>
        </div>}

      {/* --------------------------------------------------------- modals -- */}
      <LeadDialog open={leadOpen} onClose={() => setLeadOpen(false)} campusId={rep.campusId} areaId={rep.areaId} onSave={lead => {
      network.addLead({
        ...lead,
        repId: rep.id,
        campusId: rep.campusId,
        areaId: rep.areaId
      });
      setLeadOpen(false);
      setToast('Lead added to the acquisition pipeline');
    }} data-id="element-9536" data-mp="24bysz0ne4drs" />

      <IntelDialog open={intelOpen} onClose={() => setIntelOpen(false)} onSave={report => {
      network.addIntel({
        ...report,
        repId: rep.id,
        campusId: rep.campusId,
        areaId: rep.areaId
      });
      setIntelOpen(false);
      setToast('Observation submitted for review');
    }} data-id="element-9537" data-mp="3tskobamnkqev" />

      <IncidentDialog open={incidentOpen} onClose={() => setIncidentOpen(false)} onSave={incident => {
      network.reportIncident({
        ...incident,
        repId: rep.id,
        campusId: rep.campusId
      });
      setIncidentOpen(false);
      setToast('Incident reported. Trust and safety have been notified.');
    }} data-id="element-9538" data-mp="ld0x0h5jli2g" />

      <QuizDialog moduleId={quizId} onClose={() => setQuizId(null)} onPass={moduleId => {
      network.completeTraining(rep.id, moduleId);
      setQuizId(null);
      setToast('Assessment passed. Certificate issued.');
    }} data-id="element-9539" data-mp="6hkzu39o161u" />
    </div>;
}

/* ------------------------------------------------------------- dialogs -- */

function LeadDialog({
  open,
  onClose,
  onSave
}: {
  open: boolean;
  onClose: () => void;
  campusId: string;
  areaId: string | null;
  onSave: (lead: {
    kind: 'Landlord' | 'Property manager' | 'Agent' | 'Hostel' | 'Business';
    name: string;
    contactMasked: string;
    stage: 'Created';
    notes: string;
    followUpOn: string;
    category?: string;
  }) => void;
}) {
  const [kind, setKind] = useState<'Landlord' | 'Property manager' | 'Agent' | 'Hostel' | 'Business'>('Landlord');
  const [name, setName] = useState('');
  const [contact, setContact] = useState('');
  const [notes, setNotes] = useState('');
  const [followUp, setFollowUp] = useState('');
  return <Modal open={open} onClose={onClose} title="Add a lead" data-id="element-9540" data-mp="gj5ijaj4g2i9">
      <div className="space-y-3" data-id="element-9541" data-mp="liid6hg8idve">
        <Field label="Type" data-id="element-9542" data-mp="lirttefe38of">
          <Select value={kind} onChange={event => setKind(event.target.value as typeof kind)} data-id="element-9543" data-mp="li058nk7mb7n">
            <option data-id="element-9544" data-mp="1x8bwbr318yg">Landlord</option>
            <option data-id="element-9545" data-mp="3u0irpxkqv6sf">Property manager</option>
            <option data-id="element-9546" data-mp="3u1u58p3zrswq">Agent</option>
            <option data-id="element-9547" data-mp="3u2ls9bjiwibe">Hostel</option>
            <option data-id="element-9548" data-mp="bvfhjyfx84l5">Business</option>
          </Select>
        </Field>
        <Field label="Name" data-id="element-9549" data-mp="bsjjma42pfwp">
          <Input value={name} onChange={event => setName(event.target.value)} data-id="element-9550" data-mp="6v9vcxsf7aag" />
        </Field>
        <Field label="Contact number" hint="Stored for the acquisition team and masked elsewhere." data-id="element-9551" data-mp="kv2iyk8nhae7">
          <Input value={contact} onChange={event => setContact(event.target.value)} data-id="element-9552" data-mp="guioxpjdimwa" />
        </Field>
        <Field label="What you know" data-id="element-9553" data-mp="ulx9w1wucy5z">
          <Textarea rows={3} value={notes} onChange={event => setNotes(event.target.value)} data-id="element-9554" data-mp="ukvrtdherber" />
        </Field>
        <Field label="Follow up on" data-id="element-9555" data-mp="podtowgl2bbe">
          <Input value={followUp} onChange={event => setFollowUp(event.target.value)} placeholder="17 Aug 2026" data-id="element-9556" data-mp="14jbkhkw32sqd" />
        </Field>
        <div className="flex flex-wrap gap-2" data-id="element-9557" data-mp="zinm2e5iz9m4">
          <Button variant="primary" disabled={name.trim().length === 0 || contact.trim().length === 0} onClick={() => onSave({
          kind,
          name: name.trim(),
          contactMasked: `${contact.slice(0, 7)}• ••• ${contact.slice(-4)}`,
          stage: 'Created',
          notes: notes.trim(),
          followUpOn: followUp.trim() || 'Not set'
        })} data-id="element-9558" data-mp="3gcij6iw276yq">
            Add lead
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-9559" data-mp="32mf020sh7tdi">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>;
}
function IntelDialog({
  open,
  onClose,
  onSave
}: {
  open: boolean;
  onClose: () => void;
  onSave: (report: {
    category: IntelCategory;
    observation: string;
    confidence: 'Low' | 'Medium' | 'High';
    expiresOn: string | null;
  }) => void;
}) {
  const [category, setCategory] = useState<IntelCategory>('Prices');
  const [observation, setObservation] = useState('');
  const [confidence, setConfidence] = useState<'Low' | 'Medium' | 'High'>('Medium');
  const [expires, setExpires] = useState('');
  return <Modal open={open} onClose={onClose} title="Submit an observation" data-id="element-9560" data-mp="rzbobq44x2vm">
      <div className="space-y-3" data-id="element-9561" data-mp="n0qrm0o3d1bx">
        <Field label="Category" data-id="element-9562" data-mp="n1jrchomuqkk">
          <Select value={category} onChange={event => setCategory(event.target.value as IntelCategory)} data-id="element-9563" data-mp="n0iec7o23u07">
            {INTEL_CATEGORIES.map(item => <option key={item} data-id="element-9564" data-mp="d9e0jba4af6y">{item}</option>)}
          </Select>
        </Field>
        <Field label="What you observed" hint="Be specific. Vague reports are rejected." data-id="element-9565" data-mp="99vcw7qwvq0f">
          <Textarea rows={4} value={observation} onChange={event => setObservation(event.target.value)} data-id="element-9566" data-mp="4bk29usslbh5" />
        </Field>
        <Field label="How confident are you?" data-id="element-9567" data-mp="3qgyx36pzadzw">
          <Select value={confidence} onChange={event => setConfidence(event.target.value as typeof confidence)} data-id="element-9568" data-mp="3qi8wbz8khs84">
            <option data-id="element-9569" data-mp="2velwso3li8qt">Low</option>
            <option data-id="element-9570" data-mp="2vdaukspst91t">Medium</option>
            <option data-id="element-9571" data-mp="2vgf1vyyk8kwp">High</option>
          </Select>
        </Field>
        <Field label="Expires on" hint="Leave blank if it does not go stale." data-id="element-9572" data-mp="39dbrjx2xzwc0">
          <Input value={expires} onChange={event => setExpires(event.target.value)} data-id="element-9573" data-mp="34ftryxatetgo" />
        </Field>
        <p className="rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-[12px] leading-5 text-ink-muted" data-id="element-9574" data-mp="2gq7tyd66ika9">
          Observations are reviewed before publication. Nothing you submit here appears to students
          until a reviewer verifies it.
        </p>
        <div className="flex flex-wrap gap-2" data-id="element-9575" data-mp="2loj30longqrf">
          <Button variant="primary" disabled={observation.trim().length < 12} onClick={() => onSave({
          category,
          observation: observation.trim(),
          confidence,
          expiresOn: expires.trim() || null
        })} data-id="element-9576" data-mp="2lmpx58lv2akh">
            Submit
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-9577" data-mp="d92x7ewyhztz">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>;
}
function IncidentDialog({
  open,
  onClose,
  onSave
}: {
  open: boolean;
  onClose: () => void;
  onSave: (incident: {
    kind: string;
    detail: string;
    confidential: boolean;
  }) => void;
}) {
  const [kind, setKind] = useState('Unsafe property');
  const [detail, setDetail] = useState('');
  const [confidential, setConfidential] = useState(true);
  return <Modal open={open} onClose={onClose} title="Report an incident" data-id="element-9578" data-mp="i6b4gkgkgjgg">
      <div className="space-y-3" data-id="element-9579" data-mp="n679mn102gxl">
        <Field label="What happened" data-id="element-9580" data-mp="n5ed0evr97t6">
          <Select value={kind} onChange={event => setKind(event.target.value)} data-id="element-9581" data-mp="n8jzrngwvxlq">
            {['Threat or intimidation', 'Harassment', 'Unsafe property', 'Suspicious landlord', 'Suspicious agent', 'Fake listing', 'Payment scam', 'Other'].map(item => <option key={item} data-id="element-9582" data-mp="2jtry9srs8wvo">{item}</option>)}
          </Select>
        </Field>
        <Field label="Details" data-id="element-9583" data-mp="2f0ozn685ex8m">
          <Textarea rows={4} value={detail} onChange={event => setDetail(event.target.value)} data-id="element-9584" data-mp="2a36snldoys39" />
        </Field>
        <label className="flex items-start gap-2.5" data-id="element-9585" data-mp="253u8j8e7fz4d">
          <input type="checkbox" checked={confidential} onChange={event => setConfidential(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-9586" data-mp="255dm2r8h7w2g" />
          <span className="text-[12px] leading-5 text-ink-muted" data-id="element-9587" data-mp="1r4ayub5qwfpy">
            Keep my name off this report. Trust and safety will still see it, but your lead will not.
          </span>
        </label>
        <div className="flex flex-wrap gap-2" data-id="element-9588" data-mp="3lic7tov0ej3r">
          <Button variant="primary" disabled={detail.trim().length < 12} onClick={() => onSave({
          kind,
          detail: detail.trim(),
          confidential
        })} data-id="element-9589" data-mp="3ljncdqwa3uvx">
            Send report
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-9590" data-mp="1th1bjdqnqie">
            Cancel
          </Button>
        </div>
      </div>
    </Modal>;
}
function QuizDialog({
  moduleId,
  onClose,
  onPass
}: {
  moduleId: string | null;
  onClose: () => void;
  onPass: (moduleId: string) => void;
}) {
  const module = trainingModules.find(item => item.id === moduleId) ?? null;
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [result, setResult] = useState<'unmarked' | 'pass' | 'fail'>('unmarked');
  if (!module) return null;
  const allAnswered = module.quiz.every((_, index) => answers[index] !== undefined);
  return <Modal open={moduleId !== null} onClose={onClose} title={module.title} data-id="element-9591" data-mp="zbh65aob7whu">
      <div className="space-y-4" data-id="element-9592" data-mp="13cj6xvxvkglx">
        {module.quiz.map((question, index) => <fieldset key={question.question} data-id="element-9593" data-mp="13em36xpmf5z3" data-mpi={index}>
            <legend className="text-[13px] font-medium text-ink" data-id="element-9594" data-mp="11ukjzcnfdojj">{question.question}</legend>
            <div className="mt-2 space-y-1.5" data-id="element-9595" data-mp="wwsusqe60gj1">
              {question.options.map((option, optionIndex) => <label key={option} className="flex items-start gap-2.5" data-id="element-9596" data-mp="1bul430t4c8os" data-mpi={optionIndex}>
                  <input type="radio" name={`q-${index}`} checked={answers[index] === optionIndex} onChange={() => {
              setAnswers(current => ({
                ...current,
                [index]: optionIndex
              }));
              setResult('unmarked');
            }} className="mt-0.5 h-4 w-4 shrink-0 accent-brand-700" data-id="element-9597" data-mp="16uqh38cgs390" />
                  <span className="text-[13px] leading-6 text-ink" data-id="element-9598" data-mp="1plgj6uu7nm0t">{option}</span>
                </label>)}
            </div>
            {result !== 'unmarked' && answers[index] !== question.answerIndex && <p className="mt-1 text-[12px] leading-5 text-danger-700" data-id="element-9599" data-mp="1zh1g4ekf9w3p">
                Not correct. The right answer is: {question.options[question.answerIndex]}
              </p>}
          </fieldset>)}

        {result === 'fail' && <p className="flex items-start gap-2 rounded-lg border border-danger-100 bg-danger-50 px-3.5 py-3 text-[12px] leading-5 text-danger-700" data-id="element-9600" data-mp="295ovrt9fc5xk">
            <AlertTriangleIcon className="mt-0.5 h-3.5 w-3.5 shrink-0" data-id="element-9601" data-mp="2e7nugpu7yeo6" />
            Not passed. Read the corrections above and try again — this module gates real field work.
          </p>}

        <div className="flex flex-wrap gap-2" data-id="element-9602" data-mp="249hzks6ty8st">
          <Button variant="primary" disabled={!allAnswered} onClick={() => {
          const passed = module.quiz.every((question, index) => answers[index] === question.answerIndex);
          if (passed) onPass(module.id);else setResult('fail');
        }} data-id="element-9603" data-mp="1qha20y7q2dt2">
            Submit answers
          </Button>
          <Button variant="ghost" onClick={onClose} data-id="element-9604" data-mp="3n6uzfhmptdi6">
            Close
          </Button>
        </div>
      </div>
    </Modal>;
}

/** Exported so the shell can show a task count without recomputing the page. */
export { ClipboardListIcon as RepIcon };